export const PORTAL_ORIGIN = 'https://lernen.kaelte-klar.de';
export const PORTAL_HASH = '#lernen';
export const PORTAL_TIMEOUT_MS = 25000;
export const PORTAL_LOADING_ROLE_KEY = 'kk-loading-role-v1';
export const PORTAL_LOADING_ROLE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
export const PORTAL_LOGIN_HINT_KEY = 'kk-portal-login-v1';
export const PORTAL_PASSWORD_AUTH_URL = `${PORTAL_ORIGIN}/api/auth/shopify/start?returnTo=%2F%3Fauth%3Dsetup-password`;

function validLoadingRoleHint(value, now = Date.now()) {
  return value && typeof value === 'object' && !Array.isArray(value) &&
    Object.keys(value).length === 2 && ['admin', 'instructor', 'learner'].includes(value.role) &&
    Number.isFinite(value.updatedAt) && value.updatedAt <= now && now - value.updatedAt < PORTAL_LOADING_ROLE_MAX_AGE_MS;
}

// Keep in sync with app/portalNavigation.ts (predicate parity is tested).
export function prefersNativeLearningPage(browser) {
  return browser.navigator?.standalone === true ||
    browser.matchMedia?.('(display-mode: standalone)').matches === true ||
    browser.matchMedia?.('(hover: none) and (pointer: coarse)').matches === true;
}

export function portalFrameUrl(parentSearch = '') {
  const params = new URLSearchParams(parentSearch);
  if (params.size === 1 && params.get('kk-portal') === 'setup-password') {
    return `${PORTAL_ORIGIN}/?auth=setup-password`;
  }
  return `${PORTAL_ORIGIN}/`;
}

function hasControlCharacters(value) {
  return [...value].some(character => {
    const code = character.codePointAt(0);
    return code < 32 || (code >= 127 && code <= 159) ||
      (code >= 0x202a && code <= 0x202e) || (code >= 0x2066 && code <= 0x2069);
  });
}

export function validatePortalCheckout(value) {
  if (typeof value !== 'string' || value.length > 16384 || /[\s\\]/.test(value) ||
      !/^https:\/\/(?:www\.)?kaelte-klar\.de\//.test(value)) return null;
  try {
    const url = new URL(value);
    if (!['https://www.kaelte-klar.de', 'https://kaelte-klar.de'].includes(url.origin) ||
        url.username || url.password || url.port || url.hash ||
        !/^\/cart\/[1-9]\d{0,19}:1$/.test(url.pathname)) return null;
    const keys = [...url.searchParams.keys()];
    const allowed = ['properties', 'checkout[email]', 'attributes[kk_display_name]'];
    if (keys.length !== allowed.length || new Set(keys).size !== keys.length ||
        keys.some((key) => !allowed.includes(key))) return null;
    const properties = url.searchParams.get('properties');
    const email = url.searchParams.get('checkout[email]');
    const name = url.searchParams.get('attributes[kk_display_name]');
    if (!/^[A-Za-z0-9_-]{1,8192}$/.test(properties) || !email || email.length > 254 ||
        !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email) || hasControlCharacters(email) ||
        !name?.trim() || [...name].length > 80 || hasControlCharacters(name)) return null;
    return url.href;
  } catch {
    return null;
  }
}

export function readPortalMessage(event, frameWindow) {
  if (!frameWindow || event.source !== frameWindow || event.origin !== PORTAL_ORIGIN ||
      !event.data || typeof event.data !== 'object' || Array.isArray(event.data)) return null;
  const keys = Object.keys(event.data);
  if (event.data.type === 'kk-portal-loading') {
    return keys.length === 2 && keys.includes('role') && ['neutral', 'admin', 'instructor', 'learner'].includes(event.data.role)
      ? { type: 'kk-portal-loading', role: event.data.role } : null;
  }
  if (event.data.type === 'kk-portal-ready' || event.data.type === 'kk-portal-login' || event.data.type === 'kk-portal-home' || event.data.type === 'kk-portal-auth' || event.data.type === 'kk-portal-volt-admin') {
    return keys.length === 1 ? { type: event.data.type } : null;
  }
  if (event.data.type !== 'kk-portal-checkout' || keys.length !== 2 || !keys.includes('url')) return null;
  const url = validatePortalCheckout(event.data.url);
  return url ? { type: 'kk-portal-checkout', url } : null;
}

export function installPortal(dialog, window, document) {
  if (!dialog || dialog.dataset.kkPortalBound === 'true' ||
      !['https://www.kaelte-klar.de', 'https://kaelte-klar.de'].includes(window.location.origin)) return;
  dialog.dataset.kkPortalBound = 'true';
  const frameHost = dialog.querySelector('[data-kk-portal-frame]');
  const loading = dialog.querySelector('[data-kk-portal-loading]');
  const spinner = dialog.querySelector('[data-kk-portal-spinner]');
  const title = dialog.querySelector('[data-kk-portal-status-title]');
  const description = dialog.querySelector('[data-kk-portal-status-text]');
  const retry = dialog.querySelector('[data-kk-portal-retry]');
  const close = dialog.querySelector('[data-kk-portal-close]');
  if (![frameHost, loading, spinner, title, description, retry, close].every(Boolean)) return;
  let frame = null;
  let timer = 0;
  let returnFocus = null;
  let scrollPosition = null;
  let ready = false;
  let themeColors = null;
  let loadingRoleHint = null;
  let loginHint = null;

  // This only reveals the public login UI sooner. The app still checks its session.
  const rememberLogin = (confirmed) => {
    loginHint = confirmed ? { updatedAt: Date.now() } : null;
    try {
      if (loginHint) window.sessionStorage?.setItem(PORTAL_LOGIN_HINT_KEY, JSON.stringify(loginHint));
      else window.sessionStorage?.removeItem(PORTAL_LOGIN_HINT_KEY);
    } catch { /* Display only. */ }
  };

  const hasKnownLogin = () => {
    try {
      const stored = window.sessionStorage?.getItem(PORTAL_LOGIN_HINT_KEY);
      if (stored) loginHint = JSON.parse(stored);
    } catch { loginHint = null; }
    const now = Date.now();
    if (loginHint && typeof loginHint === 'object' && !Array.isArray(loginHint) &&
        Object.keys(loginHint).length === 1 && Number.isFinite(loginHint.updatedAt) &&
        loginHint.updatedAt <= now && now - loginHint.updatedAt < PORTAL_LOADING_ROLE_MAX_AGE_MS) return true;
    rememberLogin(false);
    return false;
  };

  // A short-lived label from a previously confirmed session, never an access
  // decision. Keep an in-memory fallback when browser storage is unavailable.
  const readLoadingRole = () => {
    try {
      const stored = window.sessionStorage?.getItem(PORTAL_LOADING_ROLE_KEY);
      if (stored) loadingRoleHint = JSON.parse(stored);
    } catch { /* Storage can be unavailable in privacy modes. */ }
    if (validLoadingRoleHint(loadingRoleHint)) return loadingRoleHint.role;
    loadingRoleHint = null;
    try { window.sessionStorage?.removeItem(PORTAL_LOADING_ROLE_KEY); } catch { /* Display only. */ }
    return 'neutral';
  };

  const rememberLoadingRole = (role) => {
    loadingRoleHint = role === 'neutral' ? null : { role, updatedAt: Date.now() };
    try {
      if (loadingRoleHint) window.sessionStorage?.setItem(PORTAL_LOADING_ROLE_KEY, JSON.stringify(loadingRoleHint));
      else window.sessionStorage?.removeItem(PORTAL_LOADING_ROLE_KEY);
    } catch { /* Failed storage must not block login or loading. */ }
  };

  const showLoadingRole = (role = 'neutral') => {
    const prefix = role === 'neutral' ? 'loading' : `loading${role[0].toUpperCase()}${role.slice(1)}`;
    title.textContent = dialog.dataset[`${prefix}Title`] || dialog.dataset.loadingTitle;
    description.textContent = dialog.dataset[`${prefix}Text`] || dialog.dataset.loadingText;
    dialog.dataset.loadingRole = role;
  };

  const setPortalChrome = () => {
    if (themeColors) return;
    themeColors = [...document.querySelectorAll('meta[name="theme-color"]')].map(meta => ({
      meta, content: meta.getAttribute('content'),
    }));
    if (!themeColors.length) {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.append(meta);
      themeColors.push({ meta, content: null, created: true });
    }
    for (const { meta } of themeColors) meta.setAttribute('content', '#061b2b');
  };

  const restorePageChrome = () => {
    for (const { meta, content, created } of themeColors || []) {
      if (created) meta.remove();
      else if (content === null) meta.removeAttribute('content');
      else meta.setAttribute('content', content);
    }
    themeColors = null;
  };

  const clearTimer = () => {
    window.clearTimeout(timer);
    timer = 0;
  };

  const syncViewport = () => {
    if (!dialog.open) return;
    const viewport = window.visualViewport;
    if (viewport && viewport.scale <= 1.01) {
      dialog.style.setProperty('--kk-portal-height', `${viewport.height}px`);
      dialog.style.setProperty('--kk-portal-top', `${viewport.offsetTop}px`);
    } else {
      dialog.style.removeProperty('--kk-portal-height');
      dialog.style.removeProperty('--kk-portal-top');
    }
  };

  const disposeFrame = () => {
    clearTimer();
    frame?.remove();
    frame = null;
    ready = false;
  };

  const failed = () => {
    if (!dialog.open || ready) return;
    clearTimer();
    loading.hidden = false;
    close.hidden = false;
    frameHost.inert = true;
    spinner.hidden = true;
    title.textContent = dialog.dataset.timeoutTitle;
    description.textContent = dialog.dataset.timeoutText;
    retry.hidden = false;
  };

  const load = () => {
    disposeFrame();
    const role = readLoadingRole();
    if (role !== 'neutral') rememberLogin(false);
    const knownLogin = role === 'neutral' && hasKnownLogin();
    close.hidden = knownLogin;
    frameHost.inert = !knownLogin;
    frameHost.setAttribute('aria-busy', 'true');
    loading.hidden = knownLogin;
    spinner.hidden = false;
    retry.hidden = true;
    showLoadingRole(role);
    frame = document.createElement('iframe');
    frame.title = document.getElementById(dialog.getAttribute('aria-labelledby'))?.textContent || '';
    frame.src = portalFrameUrl(window.location.search);
    frame.referrerPolicy = 'no-referrer';
    frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-downloads allow-popups allow-popups-to-escape-sandbox');
    frame.setAttribute('allow', 'fullscreen; clipboard-write');
    frame.setAttribute('allowfullscreen', '');
    const loadingFrame = frame;
    frame.addEventListener('error', () => { if (frame === loadingFrame) failed(); });
    frameHost.append(frame);
    timer = window.setTimeout(failed, PORTAL_TIMEOUT_MS);
    if (!knownLogin) close.focus({ preventScroll: true });
  };

  const cleanup = () => {
    disposeFrame();
    document.documentElement.classList.remove('kk-portal-open');
    document.body.classList.remove('kk-portal-open');
    restorePageChrome();
    if (scrollPosition) window.scrollTo({ left: scrollPosition.x, top: scrollPosition.y, behavior: 'instant' });
    scrollPosition = null;
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    returnFocus = null;
  };

  const closePortal = () => {
    const search = new URLSearchParams(window.location.search);
    if (window.location.hash === PORTAL_HASH || (dialog.open && search.has('kk-portal'))) {
      const hash = window.location.hash === PORTAL_HASH ? '' : window.location.hash;
      search.delete('kk-portal');
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${search.size ? `?${search}` : ''}${hash}`);
    }
    if (dialog.open) dialog.close();
    cleanup();
  };

  const syncHash = () => {
    if (document.documentElement.classList.contains('kk-homescreen-launch')) return;
    if (window.location.hash !== PORTAL_HASH) {
      if (dialog.open) closePortal();
      return;
    }
    if (window.parent === window && prefersNativeLearningPage(window)) {
      // Replace the #lernen history entry so Back returns to the shop, not to
      // another redirect. Never escape a device-preview iframe or copy tokens.
      window.location.replace(portalFrameUrl(window.location.search));
      return;
    }
    if (typeof dialog.showModal !== 'function') return;
    if (dialog.open) return;
    returnFocus = document.activeElement;
    scrollPosition = { x: window.scrollX, y: window.scrollY };
    document.body.append(dialog);
    dialog.showModal();
    document.documentElement.classList.add('kk-portal-open');
    document.body.classList.add('kk-portal-open');
    setPortalChrome();
    syncViewport();
    load();
  };

  window.addEventListener('message', (event) => {
    if (!dialog.open) return;
    const message = readPortalMessage(event, frame?.contentWindow);
    if (message?.type === 'kk-portal-loading') {
      // Only presentation from this exact frame; never grants access or extends a timeout.
      // Neutral is sent only after confirmed logout, not while a session is pending.
      rememberLoadingRole(message.role);
      if (message.role !== 'neutral') rememberLogin(false);
      if (!ready && !retry.hidden) return;
      if (!ready) {
        showLoadingRole(message.role);
        if (message.role !== 'neutral') {
          loading.hidden = false;
          close.hidden = false;
          frameHost.inert = true;
        }
      }
    } else if (message?.type === 'kk-portal-login') {
      rememberLoadingRole('neutral');
      rememberLogin(true);
    } else if (message?.type === 'kk-portal-ready') {
      if (ready) return;
      ready = true;
      clearTimer();
      loading.hidden = true;
      retry.hidden = true;
      close.hidden = true;
      frameHost.inert = false;
      frameHost.setAttribute('aria-busy', 'false');
      frame.focus({ preventScroll: true });
    } else if (message?.type === 'kk-portal-home') {
      closePortal();
    } else if (message?.type === 'kk-portal-auth') {
      rememberLogin(false);
      window.location.assign(PORTAL_PASSWORD_AUTH_URL);
    } else if (message?.type === 'kk-portal-volt-admin' && ready) {
      window.location.assign('https://volt-klar.de/lernen?login=admin');
    } else if (message?.type === 'kk-portal-checkout' && ready) {
      window.location.assign(message.url);
    }
  });
  window.addEventListener('hashchange', syncHash);
  window.addEventListener('pageshow', syncHash);
  window.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('resize', syncViewport);
  window.visualViewport?.addEventListener('scroll', syncViewport);
  close.addEventListener('click', closePortal);
  retry.addEventListener('click', load);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePortal();
  });
  dialog.addEventListener('close', () => { if (!dialog.open) closePortal(); });
  syncHash();
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  installPortal(document.querySelector('[data-kk-portal]'), window, document);
}
